import Swal from "sweetalert2";
import PropTypes from 'prop-types';
import styled from 'styled-components';
import EditIcon from 'mdi-react/EditIcon';
import { Spinner } from 'react-bootstrap';
import Badge from '@/shared/components/Badge';
import DeleteIcon from 'mdi-react/DeleteIcon';
import EditUserLicense from './EditUserLicense';
import React, { useState, useEffect } from 'react';
import AdminInterface from '@/utils/AdminInterface';
import withReactContent from "sweetalert2-react-content";
import { paddingLeft, paddingRight } from '@/utils/directions';
import ReactTableBase from '@/shared/components/table/ReactTableBase';
import { colorAdditional, colorGreenHover, colorAccent } from '@/utils/palette';
import { Card, CardBody, CardTitleWrap, CardTitle } from '@/shared/components/Card';

const CartTableButton = styled.button`
  background: transparent;
  border: none;
  line-height: 14px;
  ${paddingLeft}: 20px;
  ${paddingRight}: 0;
  padding-top: 0;
  padding-bottom: 0;
  color: ${colorAdditional};
  cursor: pointer;
  transition: all 0.3s;

  svg {
    height: 20px;
    width: 20px;
    fill: ${colorAdditional};
    transition: all 0.3s;
  }

  svg:hover {
    fill: ${colorGreenHover};
  }

  .orange-hover:hover {
    fill: #e9c886;
  }

  .red-hover:hover {
    fill: #e98686;
  }
`;

const TableSpinner = styled(Spinner)`
  color: ${colorAccent};
`;

const StatusFormatter = value => (
    value ? <Badge bg="success">Enable</Badge> : <Badge bg="secondary">Disable</Badge>
);

StatusFormatter.propTypes = {
    value: PropTypes.bool.isRequired,
};

const LicensesList = (props) => {
    const [showLoader, setShowLoader] = useState(true);
    const [licensesList, setLicensesList] = useState({ tableHeaderData: [], tableRowsData: [] });
    const tableConfig = {
        isEditable: false,
        isSortable: true,
        isResizable: false,
        withPagination: false,
        withSearchEngine: true,
        placeholder: 'Search...',
    };

    const handleEdit = (license) => {
        props.setSelectedLicense(license)
    }

    const handleDelete = (license) => {
        Swal.fire({
            title: 'Warning!',
            text: `Do you want to delete the license ${license.name}?`,
            icon: 'warning',
            confirmButtonText: 'Yes!',
            cancelButtonText: 'No!',
            showCancelButton: true
        }).then((result) => {
            if (result.isConfirmed) {
                AdminInterface.instance.deleteLicense(license.id).then(() => {
                    withReactContent(Swal).fire({
                        title: "Success",
                        html: "License is deleted!",
                    });
                    props.setListRequiresReload(true);
                }).catch((error) => {
                    withReactContent(Swal).fire({
                        title: "Error",
                        html: error.response?.data?.detail,
                    });
                })
            }
        });
    }

    const GetLicenses = () => {
        setShowLoader(true);
        AdminInterface.instance.getLicenses().then((response) => {
            const columns = [
                {
                    accessor: 'id',
                    Header: 'Id',
                },
                {
                    accessor: 'name',
                    Header: 'Name',
                },
                {
                    accessor: 'link',
                    Header: 'Link',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'is_open_data',
                    Header: 'Is open data?',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'edit',
                    Header: 'Edit',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'delete',
                    Header: 'Delete',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                }
            ];
            const data = [];
            response.data.forEach(license => {
                data.push({
                    id: license.id,
                    name: license.name,
                    link: <a href={license.link} target="_blank">{license.link}</a>,
                    is_open_data: StatusFormatter(license.is_open_data),
                    edit: <CartTableButton type="button" onClick={() => handleEdit(license)}><EditIcon className="orange-hover"/></CartTableButton>,
                    delete: <CartTableButton type="button" onClick={() => handleDelete(license)}><DeleteIcon className="red-hover"/></CartTableButton>,
                });
            });

            setLicensesList({ tableHeaderData: columns, tableRowsData: data });
        }).catch((error) => {
            withReactContent(Swal).fire({
                title: "Error",
                html: error.response?.data?.detail,
            });
        }).finally(() => {
            setShowLoader(false);
            props.setListRequiresReload(false);
        });
    }

    useEffect(() => {
        GetLicenses();
    }, [props.listRequiresReload]);

    return (
        <Card>
            <CardBody>
                <CardTitleWrap>
                    <CardTitle>Licenses List</CardTitle>
                </CardTitleWrap>
                {
                    !showLoader &&
                    <ReactTableBase columns={licensesList.tableHeaderData} data={licensesList.tableRowsData} tableConfig={tableConfig} />
                }
                {showLoader && (<div className="text-center"><TableSpinner animation="border" /></div>)}
            </CardBody>
            {props.selectedLicense && <EditUserLicense setSelectedLicense={props.setSelectedLicense} selectedLicense={props.selectedLicense} />}
        </Card>
    );
};

export default LicensesList;